import { useState, useEffect, useRef, type ReactNode } from "react";
import { cx } from "shared/utils";

const TRANSITION_DURATION = 200;

const isNotExited = (i: { state: "enter" | "exit" }) => i.state !== "exit";

const getSlideClass = (
  state: "enter" | "exit",
  direction: "left" | "right"
) => {
  if (state === "enter")
    return direction === "right"
      ? "slide-enter-forward"
      : "slide-enter-backward";
  return direction === "right" ? "slide-exit-forward" : "slide-exit-backward";
};

export const SlideTransition = ({
  transitionKey,
  direction,
  children
}: {
  transitionKey: string | number;
  direction: "left" | "right";
  children: ReactNode;
}) => {
  const [items, setItems] = useState<
    { key: string | number; content: ReactNode; state: "enter" | "exit" }[]
  >([{ key: transitionKey, content: children, state: "enter" }]);

  const prevKeyRef = useRef(transitionKey);
  const dirRef = useRef(direction);
  dirRef.current = direction;

  useEffect(() => {
    if (transitionKey === prevKeyRef.current) return;
    const prevKey = prevKeyRef.current;
    prevKeyRef.current = transitionKey;

    setItems([
      {
        key: prevKey,
        content: items.find((i) => i.key === prevKey)?.content ?? null,
        state: "exit"
      },
      { key: transitionKey, content: children, state: "enter" }
    ]);

    const removeExited = () => setItems((prev) => prev.filter(isNotExited));
    const timer = setTimeout(removeExited, TRANSITION_DURATION);

    return () => clearTimeout(timer);
  }, [transitionKey]);

  useEffect(() => {
    setItems((prev) =>
      prev.map((i) =>
        i.key === transitionKey ? { ...i, content: children } : i
      )
    );
  }, [children, transitionKey]);

  return (
    <div className="relative w-full h-full">
      {items.map((item) => (
        <div
          key={item.key}
          className={cx(
            item.state === "exit" ? "absolute inset-0" : "",
            getSlideClass(item.state, dirRef.current)
          )}
        >
          {item.content}
        </div>
      ))}
    </div>
  );
};
